import { useQuery } from '@tanstack/react-query';
import { TYPES } from '@blisspointmedia/bpm-types/dist/LinearBuying';
import { AdminLambdaFetch, awaitJSON } from '@src/utils/fetch-utils';
import { MediaTypeDimension, PrimaryDimension } from '../linear-pacing-constants';

interface SpendInfo {
  spend: number;
  days: Record<string, { spend: number }>;
}

// One row of /getLinearPacingSummary. The grouping dimension (company or network) is a string and
// the other dimension holds the nested child rows.
export interface SummaryRow {
  company: string | SummaryRow[];
  network: string | SummaryRow[];
  campaign: string | null;
  tvBudget: number;
  commission: number | null;
  assumedClearanceRate: number;
  hasPrelogs?: boolean;

  logs: SpendInfo; // "All" (Unsecured + Secured) & "Accepted"
  logsAll: SpendInfo; // "All" tab (Unsecured + Secured) & "All" (Accepted+)
  securedLogs: SpendInfo; // "Secured" tab & "Accepted"
  securedLogsAll: SpendInfo; // "Secured" tab & "All" (Accepted+)
  unsecuredLogs: SpendInfo; // "Unsecured" & "Accepted"
  unsecuredLogsAll: SpendInfo; // "Unsecured" & "All" (Accepted+)
  upfrontLogs: SpendInfo; // "Upfront" & "Accepted"
  upfrontLogsAll: SpendInfo; // "Upfront" & "All" (Accepted+)
  estimated: SpendInfo; // Unsecured + Secured
  estimatedAll: SpendInfo;
  booked: SpendInfo;
}

interface Spot {
  dow_iso: number;
  cost: number;
  count: number;
}

export interface BottomDataRow {
  id: number;
  avail: string;
  campaign: string;
  company: string;
  cost: number;
  count: number;
  daypart_start: string;
  daypart_end: string;
  dow: string;
  length: number;
  network: string;
  notes: string | null;
  rotation: string;
  spend: number;
  spots: Spot[];
  type: (typeof TYPES)[number];
  spend_override: number | null;
}

export const SUMMARY_QUERY_KEY = 'linearPacingSummary';
export const LOGS_QUERY_KEY = 'linearPacingCompanyNetworkLogs';

export const useLinearPacingSummary = (
  week: string,
  grouping: PrimaryDimension,
  mediaType: MediaTypeDimension
) =>
  useQuery({
    queryKey: [SUMMARY_QUERY_KEY, week, grouping, mediaType],
    enabled: Boolean(week),
    meta: { suppressGlobalError: true },
    queryFn: async (): Promise<SummaryRow[]> =>
      awaitJSON<SummaryRow[]>(
        await AdminLambdaFetch('/getLinearPacingSummary', {
          params: { week, grouping, type: mediaType.toLowerCase() }
        })
      )
  });

interface NetworkLogsParams {
  week: string;
  company: string;
  network: string;
  campaign: string;
  mediaType: MediaTypeDimension;
}

export const useCompanyNetworkLogs = (params: NetworkLogsParams | undefined) =>
  useQuery({
    queryKey: [LOGS_QUERY_KEY, params],
    enabled: Boolean(params),
    meta: { suppressGlobalError: true },
    queryFn: async (): Promise<BottomDataRow[]> => {
      if (!params) {
        return [];
      }
      const { week, company, network, campaign, mediaType } = params;
      return awaitJSON<BottomDataRow[]>(
        await AdminLambdaFetch('/getCompanyNetworkLogs', {
          params: { week, company, network, type: mediaType.toLowerCase(), campaign }
        })
      );
    }
  });

export const saveEstimateOverrides = async (rows: { id: number; spend: number }[]) => {
  await AdminLambdaFetch('/setLinearPacingEstimateOverride', {
    method: 'POST',
    body: { rows }
  });
};
